import { Box, Typography, Skeleton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { format2 } from "../../helpers/helper";
import { fetchOrderCountByVendor } from "../../services/orderAnalytics.service";

function OrderPlacementProductWise({
    dateParams,
    isDateRangeIncomplete,
}) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isDateRangeIncomplete) {
            setData([]);
            setIsLoading(false);
            return;
        }

        const { from, to } = dateParams || {};
        if (!from || !to) {
            setData([]);
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        const loadData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const list = await fetchOrderCountByVendor({ from, to });

                if (!isMounted) return;

                const formatted = list.map(item => ({
                    code: item.id,
                    value: item.orderCount || 0,
                }));

                setData(formatted);
            } catch (err) {
                if (isMounted) {
                    console.error("OrderPlacementProductWise API error", err);
                    setError("Unable to load product data");
                    setData([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [dateParams, isDateRangeIncomplete]);

    const maxValue = Math.max(...data.map(d => d.value), 1);
    const MAX_BAR_HEIGHT = 90;

    const ShimmerBars = () => (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                px: "8px",
            }}
        >
            {[1, 2, 3, 4, 5].map((i) => (
                <Box
                    key={i}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <Skeleton
                        variant="text"
                        width={30}
                        height={16}
                        sx={{ mb: 0.5 }}
                    />
                    <Skeleton
                        variant="rectangular"
                        width={18}
                        height={60 + Math.random() * 30}
                        sx={{ borderRadius: "4px" }}
                    />
                </Box>
            ))}
        </Box>
    );

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: 220,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: 12, lg: 14 },
                        color: "secondary.main",
                    }}
                >
                    Orders Placement - Product wise
                </Typography>
                <ShimmerBars />
                <Box sx={{ height: "1px", bgcolor: "divider", mx: "8px", my: "6px" }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", px: "8px" }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton
                            key={i}
                            variant="text"
                            width={30}
                            height={16}
                            sx={{ flex: 1 }}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: 220,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    minHeight: 220,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    if (!data.length) {
        return (
            <Box
                sx={{
                    minHeight: 220,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    No product data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: 220,
                p: {
                    xs: 2.5,
                    lg: 2,
                },
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                transition: "all 0.2s ease",
                "&:hover": {
                    transform: "translateY(-2px)",
                },
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Typography
                sx={{
                    fontSize: { xs: 12, lg: 14 },
                    color: "secondary.main",
                }}
            >
                Orders Placement - Product wise
            </Typography>

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        px: "8px",
                    }}
                >
                    {data.map((item) => {
                        const barHeight =
                            (item.value / maxValue) * MAX_BAR_HEIGHT;

                        return (
                            <Box
                                key={item.code}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: { xs: 12, lg: 9 },
                                        fontWeight: 500,
                                        color: "text.secondary",
                                        mb: 0.5,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {format2(item.value)}
                                </Typography>

                                <Box
                                    sx={{
                                        width: { xs: 20, lg: 18 },
                                        height: `${barHeight}px`,
                                        minHeight: 6,
                                        borderRadius: "4px",
                                        backgroundColor: "#8695F5",
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>

                <Box
                    sx={{
                        height: "1px",
                        bgcolor: "divider",
                        mx: "8px",
                        my: "6px",
                    }}
                />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        px: "8px",
                    }}
                >
                    {data.map((item) => (
                        <Typography
                            key={item.code}
                            sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "text.primary",
                                flex: 1,
                                textAlign: "center",
                            }}
                        >
                            {item.code}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default React.memo(OrderPlacementProductWise);